import { Injectable } from '@angular/core';
import { Observable, Subject, of, race, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { NgRedux } from '@redux/redux-compatibility.service';
import { AppState } from '@shared/redux/store';
import { Config } from 'src/app/config';
import { RevRule } from '@shared/models/rev.model';
import { CatDocStateTagEnum, IndElement, IndImage, IndPhrase } from '@shared/models/cat.model';
import { ReevalJobKind, ReevalJobTarget } from '@shared/models/reeval-job.model';
import { RevService } from './rev.service';
import { CatService } from './cat.service';
import { ClaimToolReturnStateService } from './claim-tool-return-state.service';

/** Tile-side state that a freshly fetched element must not overwrite. */
const TILE_STATE_KEYS = ['propagateActionFromParent', 'parentPhraseId', 'isParentActionSelected', 'viewSw', 'checkedSw'];

/** Upper bound for waiting on an in-flight page load before refreshing anyway. */
const PAGE_IDLE_TIMEOUT_MS = 15000;

@Injectable({ providedIn: 'root' })
export class ReevalTileRefreshService {

  /** Document views (rev-doc / cat-doc pages) currently mounted; curRevDoc / curCatDoc outlive them. */
  private openDocViews = new Set<ReevalJobKind>();

  /** Emits each target after its fresh data has been merged into the loaded tile objects. */
  readonly refreshed$ = new Subject<ReevalJobTarget>();

  constructor(
    private ngRedux: NgRedux<AppState>,
    private revService: RevService,
    private catService: CatService,
    private claimToolReturnStateService: ClaimToolReturnStateService
  ) {}

  setDocViewOpen(kind: ReevalJobKind, open: boolean): void {
    if (open) {
      this.openDocViews.add(kind);
    } else {
      this.openDocViews.delete(kind);
    }
  }

  /** True when the target's document page is mounted and is the document loaded in the store. */
  isDocOpen(target: ReevalJobTarget): boolean {
    if (!this.openDocViews.has(target.kind)) {
      return false;
    }
    const state = this.ngRedux.getState();
    if (target.kind === 'REV') {
      return state.curRevDoc?.docId === target.docId;
    }
    return this.catDocState(target)?.curCatDoc?.docId === target.docId;
  }

  /**
   * Re-fetches the element and merges it in place into every loaded tile object that represents it,
   * plus the store's selected element (the object an open details dialog edits).
   */
  refresh(target: ReevalJobTarget, preserveCurrentChanges = false): Observable<void> {
    return this.whenPageIdle().pipe(
      switchMap(() => {
        if (!this.isDocOpen(target)) {
          return of(undefined);
        }
        return target.kind === 'REV'
          ? this.refreshRevRule(target, preserveCurrentChanges)
          : this.refreshIndElement(target, preserveCurrentChanges);
      })
    );
  }

  /**
   * Waits for an in-flight page load (docPageLoading, set by the rule/phrase lists around getData),
   * so a page response requested before the run finished cannot overwrite the refreshed tile.
   */
  private whenPageIdle(): Observable<void> {
    const idle$ = new Observable<void>((subscriber) => {
      const check = () => {
        if (!this.ngRedux.getState().docPageLoading) {
          subscriber.next();
          subscriber.complete();
        }
      };
      const unsubscribe = this.ngRedux.subscribe(check);
      check();
      return () => unsubscribe();
    });
    return race(idle$, timer(PAGE_IDLE_TIMEOUT_MS).pipe(map(() => undefined)));
  }

  private refreshRevRule(target: ReevalJobTarget, preserveCurrentChanges: boolean): Observable<void> {
    return this.revService.getRevRuleSingle(target.docId, target.elementId, target.isImage, true).pipe(
      map((fresh: RevRule) => {
        if (!fresh || !this.isDocOpen(target)) {
          return;
        }
        const state = this.ngRedux.getState();
        const matches = this.collect([...(state.revDocRules || []), state.curRevRule], (rule: RevRule) =>
          target.isImage
            ? rule.isImage && rule.imageId == target.elementId
            : !rule.isImage && rule.phraseId == target.elementId
        );
        const currentRule = matches.find((rule: RevRule) => rule === state.curRevRule) || matches[0];
        // undo baseline = refreshed server state; taken before local changes are merged in
        fresh.saveOriginal();
        if (preserveCurrentChanges && currentRule) {
          
          this.claimToolReturnStateService.mergeCurrentChanges(currentRule, fresh);
          this.revService.recalculateRuleSections(fresh);
        }
        matches.forEach((tileRule: RevRule) => {
          const merged = Config.assetConfigData['showChildrenTiles']
            ? this.revService.setRuleChildren(tileRule, fresh)
            : fresh;
          this.assignPreservingTileState(tileRule, merged);
        });
        this.refreshed$.next(target);
      })
    );
  }

  private refreshIndElement(target: ReevalJobTarget, preserveCurrentChanges: boolean): Observable<void> {
    return this.catService.getIndElementSingle(target.docId, target.elementId, target.isImage, true).pipe(
      map((fresh: IndElement) => {
        if (!fresh || !this.isDocOpen(target)) {
          return;
        }
        const state = this.ngRedux.getState();
        const catDocState = this.catDocState(target);
        const roots = [...(catDocState.indDocPhrases || []), ...(catDocState.indDocImages || []), state.curCatElement];
        const matches = this.collect(roots, (element: IndElement) =>
          (target.isImage ? element instanceof IndImage : element instanceof IndPhrase) &&
          element.elementId == target.elementId
        );
        const currentElement = matches.find((element: IndElement) => element === state.curCatElement) || matches[0];
        // undo baseline = refreshed server state; taken before local changes are merged in
        fresh.saveOriginal();
        if (preserveCurrentChanges && currentElement) {
          
          this.claimToolReturnStateService.mergeCurrentChanges(currentElement, fresh);
          this.catService.recalculateElementSections(fresh);
        }
        matches.forEach((tileElement: IndElement) => {
          let merged = fresh;
          if (Config.assetConfigData['showChildrenTiles']) {
            if (!target.isImage) {
              merged = this.catService.setIndElementChildren(tileElement as IndPhrase, fresh as IndPhrase);
            } else if (tileElement.parentPhraseId != null) {
              this.catService.removeParentPhraseFromImageArrays(fresh as IndImage, tileElement.parentPhraseId);
            }
          }
          this.assignPreservingTileState(tileElement, merged);
        });
        this.refreshed$.next(target);
      })
    );
  }

  private catDocState(target: ReevalJobTarget) {
    const state = this.ngRedux.getState();
    return target.stateTag === CatDocStateTagEnum.SEC ? state.curCatDocStateSec : state.curCatDocStatePrime;
  }

  /** Depth-first search through top-level tiles and their children / contentChildren / imageChildren. */
  private collect(roots: any[], match: (item: any) => boolean): any[] {
    const found: any[] = [];
    const seen = new Set<any>();
    const visit = (items: any[]) => {
      (items || []).forEach((item) => {
        if (!item || seen.has(item)) {
          return;
        }
        seen.add(item);
        if (match(item)) {
          found.push(item);
        }
        Object.values(item.children || {}).forEach((children: any[]) => visit(children));
        visit(item.contentChildren);
        visit(item.imageChildren);
      });
    };
    visit(roots);
    return found;
  }

  private assignPreservingTileState(tileObject: any, fresh: any): void {
    const preserved: { [key: string]: any } = {};
    TILE_STATE_KEYS.filter((key) => key in tileObject).forEach((key) => preserved[key] = tileObject[key]);
    Object.assign(tileObject, fresh);
    Object.assign(tileObject, preserved);
  }
}
